import type { IOrdersEdgesNode } from "../queries/customer-gql";
import type {
  CartAutomaticDiscountAllocation,
  CartCodeDiscountAllocation,
  CartCustomDiscountAllocation,
  CartLine,
  ComponentizableCartLine,
  Order,
  OrderLineItem,
} from "@shopify/hydrogen-react/storefront-api-types";
import type { IProductDetailQuery } from "../queries/product-gql";
import type { ICartEdge } from "../queries/cart-gql";
import type { ILineItem, IReturnLineItem } from "../api/fetchOrder";
import type { IDraftOrderCalculateLineItem } from "../api/calculateDraftOrder";

export enum ReturnStatus {
  Requested = "requested",
  Submitted = "submitted",
  Approved = "approved",
  Closed = "closed",
}

export enum OrderStatusButtonType {
  CANCEL = "CANCEL", // 취소 버튼
  RETURN = "RETURN", // 반품 버튼
  NONE = "NONE", // 버튼 없음
}

// TODO: Partially -> Partially_Refunded 로 수정
// fulfillmentStatus
export enum OrderStatus {
  Refunded = "Refunded", // 환불 완료
  Canceled = "Canceled", // 주문 취소
  Partially = "Partially Refunded", // 부분 환불됨
  Completed = "Completed", // 주문 완료
  Return_Requested = "Return Requested", // 반품 접수됨
  Return_Approved = "Return Approved", // 반품 승인됨
  Shipped = "Shipped", // 발송 완료
  Partially_Shipped = "Partially Shipped", // 일부 발송 완료
  Preparing = "Partially_Shipped", // 배송 준비 중
  Scheduled_Preparing = "Scheduled Preparing", // 예약 발송 준비 중
  Completed_Preparing = "Completed Preparing", // 예약 발송 완료
  PrizeCancel = "PrizeCancel", // 당첨 취소
  DrawCancel = "DrawCancel", // 응모 취소
  DrawLose = "DrawLose", // 미당첨
  DrawWin = "DrawWin", // 당첨 🎉
  DrawApply = "DrawApply", // 응모 완료
  Paid = "Paid", // 결제 완료 // TODO: 없애기
  Fulfilled = "Fulfilled",
  Unfulfilled = "Unfulfilled",
  None = "",
}

export const OrderStatusTrans: Record<OrderStatus, string> = {
  [OrderStatus.Refunded]: "order_status.refunded", // 환불 완료
  [OrderStatus.Canceled]: "order_status.canceled", // 주문 취소
  [OrderStatus.Partially]: "order_status.partially", // 부분 환불됨
  [OrderStatus.Completed]: "order_status.completed", // 결제 완료
  [OrderStatus.Return_Requested]: "order_status.return", // 반품 접수됨
  [OrderStatus.Return_Approved]: "order_status.return_approved", // 반품 승인됨
  [OrderStatus.Shipped]: "order_status.shipped", // 발송 완료
  [OrderStatus.Partially_Shipped]: "order_status.partially_shipped", // 일부 발송 완료
  [OrderStatus.Preparing]: "order_status.preparing", // 배송 준비 중
  [OrderStatus.Scheduled_Preparing]: "order_status.scheduled_shipped", // 예약 발송 준비 중
  [OrderStatus.Completed_Preparing]: "order_status.completed_shipped", // 예약 발송 완료
  [OrderStatus.Paid]: "order_status.paid", // 결제 완료
  [OrderStatus.Fulfilled]: "order_status.fulfilled",
  [OrderStatus.Unfulfilled]: "order_status.unfulfilled",
  [OrderStatus.PrizeCancel]: "order_status.prizeCancel", // 당첨 취소
  [OrderStatus.DrawCancel]: "order_status.drawCancel", // 응모 취소
  [OrderStatus.DrawLose]: "order_status.drawLose", // 미당첨
  [OrderStatus.DrawWin]: "order_status.drawWin", // 당첨 🎉
  [OrderStatus.DrawApply]: "order_status.drawApply", // 응모 완료
  [OrderStatus.None]: "", // 초기값
};

/** Represents the order's current financial status. */
// import { OrderFinancialStatus } from "@shopify/hydrogen-react/storefront-api-types";
export enum FinancialStatus {
  /** Displayed as **Authorized**. */
  Authorized = "AUTHORIZED",
  /** Displayed as **Paid**. */
  Paid = "PAID", // 결제 완료
  /** Displayed as **Partially paid**. */
  PartiallyPaid = "PARTIALLY_PAID",
  /** Displayed as **Partially refunded**. */
  PartiallyRefunded = "PARTIALLY_REFUNDED",
  /** Displayed as **Pending**. */
  Pending = "PENDING", // 미결제
  /** Displayed as **Refunded**. */
  Refunded = "REFUNDED", // 환불 완료
  /** Displayed as **Voided**. */
  Voided = "VOIDED",
}

/** Represents the order's aggregated fulfillment status for display purposes. */
// import { OrderFulfillmentStatus } from "@shopify/hydrogen-react/storefront-api-types";
export enum OrderFulfillmentStatus {
  /** Displayed as **Fulfilled**. All of the items in the order have been fulfilled. */
  Fulfilled = "FULFILLED",
  /** Displayed as **In progress**. Some of the items in the order have been fulfilled, or a request for fulfillment has been sent to the fulfillment service. */
  InProgress = "IN_PROGRESS",
  /** Displayed as **On hold**. All of the unfulfilled items in this order are on hold. */
  OnHold = "ON_HOLD",
  /** Displayed as **Open**. None of the items in the order have been fulfilled. Replaced by "UNFULFILLED" status. */
  Open = "OPEN",
  /** Displayed as **Partially fulfilled**. Some of the items in the order have been fulfilled. */
  PartiallyFulfilled = "PARTIALLY_FULFILLED",
  /** Displayed as **Pending fulfillment**. A request for fulfillment of some items awaits a response from the fulfillment service. Replaced by "IN_PROGRESS" status. */
  PendingFulfillment = "PENDING_FULFILLMENT",
  /** Displayed as **Restocked**. All of the items in the order have been restocked. Replaced by "UNFULFILLED" status. */
  Restocked = "RESTOCKED",
  /** Displayed as **Scheduled**. All of the unfulfilled items in this order are scheduled for fulfillment at later time. */
  Scheduled = "SCHEDULED",
  /** Displayed as **Unfulfilled**. None of the items in the order have been fulfilled. */
  Unfulfilled = "UNFULFILLED",
}

/** Draw Product를 포함하는 오더인지 확인 */
export const checkDrawOrder = (order?: IOrdersEdgesNode): boolean => {
  if (!order) return false;

  // Draw 태그가 있어도 Onsale 태그가 있으면 일반 상품으로 본다
  return Boolean(order.instagram);
};

/** 주문 목록 페이지에서 보여줘야 하는 주문만 필터 */
export const orderHistoryFilter = (order?: IOrdersEdgesNode): boolean => {
  if (!order) return false;

  // financialStatus가 PENDING인 주문은 보여주지 않는다.
  // Draw 오더이면서 financialStatus가 PAID가 아닌 주문은 보여주지 않는다.
  return !(
    order.financialStatus === "PENDING" ||
    (checkDrawOrder(order) &&
      order.financialStatus !== "PAID" &&
      order.financialStatus !== "REFUNDED" &&
      order.financialStatus !== "PARTIALLY_REFUNDED") ||
    order.drawStatus?.value === "lose"
  );
};

/** 주문이 송장번호를 가지고 있는지 확인 */
export const orderHasTracking = (order?: Order): boolean => {
  if (!order) return false;

  // order 데이터 아래에 successfulFulfillment와 trackingInfo가 있으면 송장이 있는 것으로 본다.
  return Boolean(order.successfulFulfillments?.[0]?.trackingInfo[0]);
};

/** 주문에 환불 기록이 있는지 확인 */
export const orderHasRefund = (order?: Order): boolean => {
  if (!order) return false;

  const orderStatus = ["REFUNDED", "PARTIALLY_REFUNDED"];

  // order 데이터 아래에 successfulFulfillment와 trackingInfo가 있으면 송장이 있는 것으로 본다.
  return orderStatus.includes(order.financialStatus as string);
};

/**
 * OrderHistory 페이지 주문상태
 * @param order - 주문 데이터
 */
export const getOrderStatus = (order: IOrdersEdgesNode): OrderStatus | undefined => {
  switch (order.financialStatus) {
    case FinancialStatus.Paid:
      // 고객이 주문 완료 후 아직 비젬 주문 확정 전일 때
      if (order.fulfillmentStatus === OrderFulfillmentStatus.Unfulfilled) return OrderStatus.Completed;
      else if (
        order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled ||
        order.fulfillmentStatus === OrderFulfillmentStatus.PartiallyFulfilled
      ) {
        // 고객이 주문 완료 후 Order History 페이지에서 반품 접수했을 때
        if (order.returnStatus?.value === ReturnStatus.Requested) return OrderStatus.Return_Requested;
        // CS팀에서 반품 승인했을 때
        if (order.returnStatus?.value === ReturnStatus.Approved) return OrderStatus.Return_Approved;

        // 주문에 프리오더 제품이 존재하고 해당 상품이 송장 전송이 되지 않았을 때
        if (hasPreorder(order) && !orderHasTracking(order)) {
          return OrderStatus.Scheduled_Preparing;
        }

        // 주문에 프리오더 제품이 존재하고 송장 전송까지 되었을 때
        if (hasPreorder(order) && orderHasTracking(order)) {
          return OrderStatus.Completed_Preparing;
        }

        // 고객이 주문 완료 후 비젬에서 송장 전송까지 했을 때
        if (orderHasTracking(order)) return OrderStatus.Shipped;

        // 고객이 주문 완료 후 비젬에서 주문 확정했을 때
        return OrderStatus.Preparing;
      }
      // 고객이 주문 완료 후 '일부 상품'을 hold 했을때
      else if (order.fulfillmentStatus === OrderFulfillmentStatus.OnHold) {
        // 고객이 주문 완료 후 Order History 페이지에서 반품 접수했을 때
        if (order.returnStatus?.value === ReturnStatus.Requested) return OrderStatus.Return_Requested;

        // 일부 상품만 비젬에서 송장 전송까지 했을때
        if (orderHasTracking(order)) return OrderStatus.Partially_Shipped;

        // 고객이 주문 완료 후 비젬에서 주문 확정했을 때
        return OrderStatus.Paid;
      }
      return OrderStatus.Paid;
    case FinancialStatus.Refunded:
      // 고객이 반품 신청 후, CS팀에서 승인한 뒤 환불 처리까지 완료했을 때
      if (!order.canceledAt) return OrderStatus.Refunded;
      // 고객 또는 CS팀에서 일반적으로 주문을 취소했을 때
      return OrderStatus.Canceled;
    case FinancialStatus.PartiallyRefunded:
      if (order.returnStatus?.value === ReturnStatus.Requested) return OrderStatus.Return_Requested;
      if (order.returnStatus?.value === ReturnStatus.Approved) return OrderStatus.Return_Approved;
      // 고객 또는 CS팀에서 일부 상품만 환불하거나 반품 처리했을 때
      return OrderStatus.Partially;
    default:
  }
};

/**
 * 할인 금액을 계산하는 함수
 *
 * @param originalPrice - 원래 가격 (할인이 적용되기 전의 가격)
 * @param membershipDiscountPercent - 등급 할인율 (회원 등급에 따른 할인 비율, %로 표시)
 * @param codeDiscountPercent - 할인 코드 할인율 (적용된 할인 코드에 따른 할인 비율, %로 표시)
 * @returns {
 *   membershipDiscountAmount: number; // 등급 할인 금액
 *   codeDiscountAmount: number; // 할인 코드 적용 후 할인 금액
 *   totalDiscount: number; // 총 할인 금액
 *   finalPrice: number; // 최종 가격 (할인 적용 후)
 * }
 */
export const getDiscountAmount = (
  originalPrice: number,
  membershipDiscountPercent: number,
  codeDiscountPercent: number,
): {
  membershipDiscountAmount: number;
  codeDiscountAmount: number;
  totalDiscount: number;
  finalPrice: number;
} => {
  // 등급 할인 계산
  const membershipDiscountAmount = (originalPrice * membershipDiscountPercent) / 100;
  // 등급 할인 적용된 가격
  const priceAfterMembershipDiscount = originalPrice - membershipDiscountAmount;

  // 할인 코드 적용 후 할인 계산
  const codeDiscountAmount = (priceAfterMembershipDiscount * codeDiscountPercent) / 100;

  // 총 할인 금액
  const totalDiscount = membershipDiscountAmount + codeDiscountAmount;

  return {
    membershipDiscountAmount,
    codeDiscountAmount,
    totalDiscount,
    finalPrice: originalPrice - totalDiscount,
  };
};

export const getDiscountName = (
  discount: CartAutomaticDiscountAllocation | CartCodeDiscountAllocation | CartCustomDiscountAllocation,
): string => {
  if (
    discount.__typename === "CartAutomaticDiscountAllocation" ||
    discount.__typename === "CartCustomDiscountAllocation"
  ) {
    return discount.title;
  } else if (discount.__typename === "CartCodeDiscountAllocation") {
    return discount.code;
  }

  return "";
};

export const getMembershipDiscountPercent = (
  discount: CartAutomaticDiscountAllocation | CartCodeDiscountAllocation | CartCustomDiscountAllocation,
): number => {
  const discountName = getDiscountName(discount);
  const numberMatch = /\d+/.exec(discountName); // 숫자 추출

  return numberMatch ? Number(numberMatch[0]) : 0;
};

/**
 * 상품 가격 정보 및 할인 정보를 계산하여 반환하는 함수
 * @param node - 카트 라인아이템 항목에 대한 정보가 포함된 객체
 * @returns 상품의 가격 정보 및 할인 정보가 포함된 객체
 *   - `price`: 기본 가격
 *   - `compareAtPrice`: 원래 가격 (할인이 적용되기 전 가격)
 *   - `isSale`: 세일 여부
 *   - `salePercent`: 세일 퍼센트
 *   - `amount`: 수량 당 비용
 *   - `discountPercent`: 할인 퍼센트
 *   - `membershipDiscountAmount`: 등급 할인 금액
 *   - `codeDiscountAmount`: 할인 코드 할인 금액
 */
export const getDiscountPriceInfo = (node: ICartEdge["node"]) => {
  const price = parseInt(node.merchandise.price.amount || "0");
  const compareAtPrice = parseInt(node.merchandise.compareAtPrice?.amount ?? "0");
  const isSale = Boolean(price && compareAtPrice && price < compareAtPrice);
  const salePercent = compareAtPrice ? ((compareAtPrice - price) / compareAtPrice) * 100 : 0;

  const amount = parseInt(node.cost.amountPerQuantity.amount || "0");
  const discountPercent =
    node.attributes && node.attributes.length
      ? Number(node.attributes.find((attr) => attr.key === "discount_percent")?.value) || 0
      : 0;

  // TODO: discountAllocations에 두 개 이상의 요소가 들어가는 경우가 있는지 확인
  // let discountAmounts = { membershipDiscountAmount: 0, codeDiscountAmount: 0 };
  //
  // // `node.discountAllocations` 배열이 존재하는지 확인
  //   if (node.discountAllocations.length > 0) {
  //     // 모든 할인 항목을 순회하면서 할인 금액을 누적
  //     node.discountAllocations.forEach(discount => {
  //       // "CartAutomaticDiscountAllocation" 타입인 경우 (멤버십 할인)
  //       if (discount.__typename === "CartAutomaticDiscountAllocation") {
  //         discountAmounts = getDiscountAmount(
  //             amount,
  //             getMembershipDiscountPercent(discount), // 멤버십 할인 비율 계산
  //             discountPercent, // 추가적인 할인 비율
  //         );
  //       }
  //       // "CartCodeDiscountAllocation" 타입인 경우 (할인 코드 할인)
  //       else if (discount.__typename === "CartCodeDiscountAllocation") {
  //         discountAmounts.codeDiscountAmount += parseInt(discount.discountedAmount.amount || "0"); // 할인 코드 할인 금액 누적
  //       }
  //       // 그 외의 할인 타입인 경우 (예: 사용자 정의 할인 등)
  //       else {
  //         // 해당 타입에서는 할인을 적용하지 않음
  //       }
  //     });
  //   }

  let discountAmounts;

  if (node.discountAllocations.length > 0) {
    // discountAllocations에 한개의 요소만 들어있다고 가정
    const discount = node.discountAllocations[0];

    // 1. 할인 항목이 "CartAutomaticDiscountAllocation" 타입인 경우
    if (discount.__typename === "CartAutomaticDiscountAllocation") {
      // 멤버십 할인 (title이 있는 경우)
      discountAmounts = getDiscountAmount(amount, getMembershipDiscountPercent(discount), discountPercent);
    }
    // 2. 할인 항목이 "CartCodeDiscountAllocation" 타입인 경우
    else if (discount.__typename === "CartCodeDiscountAllocation") {
      // 할인 코드 할인 (code가 있는 경우)
      discountAmounts = {
        membershipDiscountAmount: 0,
        codeDiscountAmount: parseInt(discount.discountedAmount.amount || "0"),
      };
    }
    // 3. 그 외의 할인 타입인 경우 (우리 스토어에서는 존재하지 않음)
    else {
      discountAmounts = { membershipDiscountAmount: 0, codeDiscountAmount: 0 };
    }
    // 4. `discountAllocations` 배열이 비어있는 경우
  } else {
    discountAmounts = { membershipDiscountAmount: 0, codeDiscountAmount: 0 };
  }

  return {
    price,
    compareAtPrice,
    isSale,
    salePercent,
    amount,
    discountPercent,
    ...discountAmounts,
  };
};

/** 주문취소, 반품/교환과 같이 고객이 주문상태를 변경 가능한지 확인 */
// TODO: showOrderStatusButton 로직 수정
export const showOrderStatusButton = (
  // order: (IOrdersEdgesNode & { returnStatus?: { value: ReturnStatus } }) | null,
  order: IOrdersEdgesNode | null,
  orderStatus?: OrderStatus | "",
  isKR?: boolean,
): boolean => {
  if (!order) return false;

  // 환불 가능 기간
  const refundMaxDate = isKR ? 14 : 31;

  // 이미 환불된 주문일 때 || 교환/반품 접수 중일 때 || order status => 'fulfilled', tracking number value is null
  if (
    order.financialStatus === FinancialStatus.Refunded ||
    (order.fulfillmentStatus === OrderFulfillmentStatus.Fulfilled &&
      !order.successfulFulfillments?.[0]?.trackingInfo[0]?.number)
  ) {
    return false;
  }

  // if (
  //   order.financialStatus === FinancialStatus.PartiallyRefunded &&
  //   order.returnStatus
  // ) {
  //   return (
  //     (order.returnStatus.value as ReturnStatus) !== ReturnStatus.Requested
  //   );
  // }

  // TODO: 튠에서만 사용하는 로직으로 확인함 -> 제거하고 리팩토링 된 useOrederStatus에 반영 예정
  // 프리오더 일 때
  // const isPreOrder =
  //   order.lineItems.edges.some((item) =>
  //     item.node.variant?.product.tags.includes("Preorder"),
  //   ) || false;
  // if (isPreOrder) {
  //   const preOrderList = order.lineItems.edges.filter(
  //     ({ node }) => node.variant?.product.tags.includes("Preorder") || false,
  //   );
  //
  //   if (preOrderList.length) {
  //     if (!preOrderList[0].node.variant) return false;
  //
  //     const preOrderData = (
  //       preOrderList[0].node.variant.product as Product & IProductMetafields
  //     ).preorder;
  //
  //     if (!preOrderData) return false;
  //
  //     const fulfillmentDate = (
  //       preOrderData.reference as Metaobject
  //     ).fields.find((field) => field.key === "fulfillment_date");
  //
  //     if (!fulfillmentDate?.value) return false;
  //
  //     const today = new Date();
  //     const date = new Date(fulfillmentDate.value);
  //
  //     // 주문 완료 || 부분 환불
  //     if (
  //       orderStatus === OrderStatus.Completed ||
  //       orderStatus === OrderStatus.Partially
  //     ) {
  //       return date > today;
  //     }
  //
  //     // 발송 완료
  //     if (orderStatus === OrderStatus.Shipped) {
  //       const diff = (today.getTime() - date.getTime()) / (24 * 60 * 60 * 1000);
  //       return diff < refundMaxDate;
  //     }
  //
  //     return false;
  //   }
  //
  //   return false;
  // }

  // 당첨된 드로우 오더일 때
  // if (checkDrawOrder(order)) return false;

  // 주문한지 9일 지났을 때
  const orderDate = new Date(order.processedAt);
  const today = new Date();
  const diff = (today.getTime() - orderDate.getTime()) / (24 * 60 * 60 * 1000);
  return diff < refundMaxDate;
};

/** 주문한 상품중에 예약발송인 상품을 가지고 있는지 확인 */
export const hasPreorder = (order: IOrdersEdgesNode | null): boolean => {
  if (!order) return false;

  return order.lineItems.edges.some((edge) =>
    (edge.node.variant?.product as IProductDetailQuery["product"])?.tags.includes("Preorder"),
  );
};

/**
 * 주문이 반품 가능한 기간 내에 있는지 확인
 * @param {IOrdersEdgesNode} order - 주문
 * @param {number} refundMaxDate - 반품이 가능한 최대 일수
 * @returns {boolean} - 현재 날짜가 반품 가능 기간 내에 있으면 `true`, 그렇지 않으면 `false`를 반환
 */
export const isReturnPeriodValid = (order: IOrdersEdgesNode, refundMaxDate: number): boolean => {
  // fulfillmentDate로 바꾸기
  const orderDate = new Date(order.processedAt);
  const today = new Date();
  const diffInDays = (today.getTime() - orderDate.getTime()) / (24 * 60 * 60 * 1000);
  return diffInDays < refundMaxDate;
};

/** 주어진 날짜 기준, 다음 달 1일 */
export const getNextMonthFirstDate = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
};

/** 주어진 날짜 기준, 정확히 1년 전 날짜 */
export const getSameDayOfPreviousYear = (date: Date): Date => {
  return new Date(date.getFullYear() - 1, date.getMonth(), date.getDate());
};

/** 주어진 날짜 기준, 해당 월의 마지막 날에서 7일을 뺀 날짜 */
export const getOneWeekBeforeMonthEnd = (date: Date): Date => {
  // 현재 달의 마지막 날 계산
  const lastDayOfCurrentMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  return new Date(date.getFullYear(), date.getMonth(), lastDayOfCurrentMonth - 7, 0, 0, 0);
};

/**
 * 주문에 할인 코드가 적용되었는지 확인
 * @param order
 * @param discountCodeValue - 할인코드
 */
export const checkOrderHasDiscountCode = (order: Order, discountCodeValue: string): boolean => {
  return order.discountApplications.edges.some(({ node: discountApplication }) => {
    return (
      discountApplication.__typename === "DiscountCodeApplication" &&
      discountApplication.code.toUpperCase() === discountCodeValue.toUpperCase()
    );
  });
};

/**
 * @description 주문 내역 line item 단위의 가격 및 할인가 계산하는 로직
 */
export const calculateLineItems = (
  lineItem: IReturnLineItem | ILineItem | OrderLineItem,
): {
  originalPrice: number;
  compareAtPrice: number;
  totalDiscountedPrice: number;
} => {
  // originalQuantity가 있으면 order complete 페이지가 아닌 모든 곳

  const originalPriceQuantity = "originalQuantity" in lineItem ? 1 : lineItem.quantity;

  // 판매가 (개당)
  const originalPrice = parseFloat(lineItem.originalTotalPrice.amount) / originalPriceQuantity;

  // 정상가 (개당)
  const compareAtPrice = parseFloat(lineItem.variant?.compareAtPrice?.amount ?? "0") || originalPrice;

  const discountAmountQuantity = "originalQuantity" in lineItem ? lineItem.originalQuantity : 1;

  // 구매 당시 할인된 금액 (개당)
  const calculatedDiscountAmount =
    lineItem.discountAllocations
      .map(({ allocatedAmount }) => parseFloat(allocatedAmount.amount))
      .reduce((a, b) => a + b, 0) / discountAmountQuantity;

  const totalDiscountQuantity = "originalQuantity" in lineItem ? lineItem.quantity : 1;

  // 구매 당시, 개당 할인 가 (판매가 * 수량 - 총 할인된 금액)
  const totalDiscountedPrice =
    (originalPrice * lineItem.quantity - calculatedDiscountAmount * totalDiscountQuantity) / lineItem.quantity;

  return { originalPrice, compareAtPrice, totalDiscountedPrice };
};

export const calculateCartLineItemsInCheckout = (
  lineItem: CartLine | ComponentizableCartLine,
  draftOrderCalculate?: {
    lineItems: IDraftOrderCalculateLineItem[];
    discountCodes: string[];
  },
): {
  lineItemPrice: number;
  appliedDiscount: { title: string; amount: number }[];
} => {
  const calculatedLineItem = draftOrderCalculate
    ? draftOrderCalculate.lineItems.find((item) => item.variant.id === lineItem.merchandise.id)
    : undefined;

  const totalAmount = calculatedLineItem
    ? Number(calculatedLineItem.discountedTotalSet.shopMoney.amount)
    : Number(lineItem.cost.totalAmount.amount);

  const appliedDiscount: { title: string; amount: number }[] = [];
  // 할인코드가 적용된 상태일 때
  if (draftOrderCalculate && calculatedLineItem) {
    const originalTotalAmount = Number(calculatedLineItem.originalTotalSet.shopMoney.amount);
    const discountedTotalAmount = Number(calculatedLineItem.discountedTotalSet.shopMoney.amount);

    // 멤버십 상시할인
    const appliedDiscountAmount = Number(calculatedLineItem.appliedDiscount?.amountSet.shopMoney.amount ?? 0);
    if (calculatedLineItem.appliedDiscount && appliedDiscountAmount > 0) {
      appliedDiscount.push({
        title: calculatedLineItem.appliedDiscount.description,
        amount: appliedDiscountAmount,
      });
    }

    // 할인코드
    const appliedDiscountCode = draftOrderCalculate.discountCodes[0];
    // 할인코드가 적용되었는지 금액 비교해서 확인
    if (originalTotalAmount - discountedTotalAmount > appliedDiscountAmount) {
      const discountTitle = appliedDiscountCode.toLowerCase();
      const discountedAmount = originalTotalAmount - discountedTotalAmount - appliedDiscountAmount;
      appliedDiscount.push({ title: discountTitle, amount: discountedAmount });
    }
  } else {
    // 할인코드가 적용되지 않은 상태일 때
    const cartAutomaticDiscount = lineItem.discountAllocations.find(
      (discount) =>
        discount.__typename === "CartAutomaticDiscountAllocation" && Number(discount.discountedAmount.amount) > 0,
    );
    if (cartAutomaticDiscount) {
      const castedCartAutomaticDiscount = cartAutomaticDiscount as CartAutomaticDiscountAllocation;
      const discountTitle = castedCartAutomaticDiscount.title.toLowerCase();
      const discountedAmount = Number(castedCartAutomaticDiscount.discountedAmount.amount);
      appliedDiscount.push({ title: discountTitle, amount: discountedAmount });
    }
  }

  return {
    lineItemPrice: totalAmount,
    appliedDiscount,
  };
};

/**
 * @description 배송 tracking 정보 조회
 */
export const getTrackingInfo = ({ order, lang }: { order?: IOrdersEdgesNode; lang: "ko" | "en" | "jp" }) => {
  if (!order) return;

  const CJ_TRACKING_URL = "https://trace.cjlogistics.com/next/tracking.html?wblNo=";

  const fulfillment = order.successfulFulfillments;
  const info = fulfillment?.[fulfillment.length - 1]?.trackingInfo[0];
  if (info) {
    const trackingUrl = lang === "ko" ? `${CJ_TRACKING_URL}${info.number ?? ""}` : (info.url ?? "");
    return { ...info, url: trackingUrl };
  }

  return null;
};
